import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

import { useAuth } from '@/app/providers/AuthProvider';
import { UserApi } from '@/services/api/userApi';
import { AUTH_SESSION_EXPIRED_EVENT } from '@/services/auth';
import { setItraceUser } from '@/services/monitoring';
import type { UserProfile } from '@/types';

interface UserContextValue {
  activeUser: UserProfile;
}

const UserContext = createContext<UserContextValue | undefined>(undefined);

interface UserProviderProps {
  children: React.ReactNode;
}

const normalizeUserProfile = (
  profile: Partial<UserProfile> & { id?: string | number; uid?: string | number },
): UserProfile => {
  const nickname = String(profile.nickname || '').trim() || undefined;
  const avatar = String(profile.avatar || '').trim() || undefined;
  const phone = String(profile.phone || '').trim() || undefined;
  return {
    id: String(profile.id ?? profile.uid ?? ''),
    created_at: String(profile.created_at || new Date('2026-01-01T00:00:00.000Z').toISOString()),
    nickname,
    avatar,
    phone,
  };
};

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [activeUser, setActiveUser] = useState<UserProfile | null>(null);

  const loadCurrentUser = useCallback(async (): Promise<void> => {
    const currentUser = await UserApi.getCurrentUser();
    setActiveUser(normalizeUserProfile(currentUser));
  }, []);

  useEffect(() => {
    const load = async () => {
      if (!isAuthenticated) {
        setActiveUser(null);
        return;
      }
      try {
        await loadCurrentUser();
      } catch (error) {
        console.error('Failed to load current user:', error);
        // 加载用户失败（可能是 token 无效），交由认证层保留当前地址并重新登录。
        window.dispatchEvent(new CustomEvent(AUTH_SESSION_EXPIRED_EVENT));
      }
    };
    void load();
  }, [loadCurrentUser, isAuthenticated]);

  // 用户确定后同步 uid 到 iTrace 监控
  useEffect(() => {
    if (activeUser) {
      setItraceUser(activeUser.id);
    }
  }, [activeUser]);

  if (!isAuthenticated) {
    // 未认证时直接渲染子组件，由 ProtectedRoute 处理跳转
    return <>{children}</>;
  }

  if (!activeUser) {
    // 已认证但无用户数据，显示 loading
    return null;
  }

  return (
    <UserContext.Provider
      value={{
        activeUser,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUser = (): UserContextValue => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within UserProvider');
  }
  return context;
};
